import { type Events, type EventsCustom, hooks } from '@/store.js';
import { BubbleMenu, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { memo, useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';
import Placeholder from '@tiptap/extension-placeholder';
import { Button } from '@heroui/react';
import TaskItem from '@tiptap/extension-task-item';
import TaskList from '@tiptap/extension-task-list';
import UploadCover from './UploadCover.jsx';
import Link from '@tiptap/extension-link';

import TypeSelect from './TypeSelect.jsx';
import UploadImages from './UploadImages.jsx';
import showErrMessage from '@/utils/showErrMessage.js';
import TimePicker from './TimePicker.jsx';

import type { IProps } from './interface.js';
import Menu from './Menu.jsx';
import { useMemoizedFn } from 'ahooks';
import MoneyInput from './MoneyInput.jsx';

const extensions = [
  StarterKit,
  Placeholder.configure({ placeholder: '添加备注' }),
  TaskList,
  TaskItem.configure({
    nested: true,
  }),
  Link.configure({
    // protocols: ['ftp', 'mailto'],
  }),
];

const ContentEdit = memo(({ data }: { data: EventsCustom }) => {
  const contentField = hooks.useField(data, 'content');
  const editor = useEditor({
    extensions,
    content: contentField.value,
    onUpdate(props) {
      contentField.setValue(props.editor.getHTML());
    },
  });

  const setLink = useMemoizedFn(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);

    // cancelled
    if (url === null) {
      return;
    }

    // empty
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();

      return;
    }

    // update link
    try {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: url })
        .run();
    } catch (e) {
      showErrMessage(e);
    }
  });

  return (
    <>
      {editor && (
        <BubbleMenu editor={editor} tippyOptions={{ duration: 100 }}>
          <div className='bg-white rounded-xl shadow-xl p-1 text-xl border border-gray-100 flex items-center'>
            {[
              {
                icon: 'format_bold',
                onClick() {
                  editor.chain().focus().toggleBold().run();
                },
                isActive: editor.isActive('bold'),
              },
              {
                icon: 'link',
                onClick() {
                  // editor.chain().focus().toggleBold().run();
                  setLink();
                },
                isActive: editor.isActive('link'),
              },
            ].map((i) => (
              <div
                key={i.icon}
                onClick={i.onClick}
                className={`px-2 py-1 rounded-lg flex items-center justify-center  ${
                  i.isActive
                    ? 'bg-blue-500 text-white'
                    : 'hover:bg-gray-50 text-gray-600'
                }`}
              >
                <i className={`ic-round md-${i.icon} leading-none`} />
              </div>
            ))}
          </div>
        </BubbleMenu>
      )}
      <EditorContent
        editor={editor}
        onBlur={contentField.inputProps.onBlur}
        onFocus={contentField.inputProps.onFocus}
        className='bg-white border border-gray-200 overflow-auto max-h-[60vh]  text-gray-700/80 text-[13px]  rounded-xl mt-2.5 py-2 px-3 hover:border-black hover:[box-shadow:0_0_0_1px_rgba(0,0,0,1)]'
      />
    </>
  );
}, isEqual);

const Inner = memo(
  ({
    data,
    requestClose,
    requestReorder,
    id,
    record,
  }: { data: EventsCustom; record: Events } & Pick<
    IProps,
    'requestClose' | 'requestReorder' | 'id'
  >) => {
    hooks.useWatch(record);

    const field = hooks.useField(data, 'title');
    const moneyField = hooks.useField(record, 'money');
    const dateField = hooks.useField(record, 'date');

    const cover = record.get('cover');

    const typeField = hooks.useField(record, 'type');

    const imagesField = hooks.useField(record, 'images');
    const startTimeField = hooks.useField(record, 'startTime');
    const endTimeField = hooks.useField(record, 'endTime');

    const [uploadImages, setUploadImages] = useState<string[]>([]);
    const images = useMemo(() => {
      return [...imagesField.value, ...uploadImages];
    }, [imagesField.value, uploadImages]);

    return (
      <div
        className='pb-2 px-4  '
        // ref={ref}
      >
        <div className='flex pt-2 '>
          <UploadCover
            id={id}
            className=' shrink-0 mr-3 '
            defaultValue={cover}
          />
          <div className='flex-grow px-1 overflow-hidden -mt-1.5  '>
            <TypeSelect
              value={typeField.value}
              onChange={typeField.setValue}
              icon={record.get('icon')}
            />

            <input
              placeholder='活动名称'
              {...field.inputProps}
              disabled={field.presence.occupied}
              className='outline-none text-xl font-bold'
            />
            <ContentEdit data={data} />
            <div className='mt-3 flex flex-wrap gap-2 '>
              <TimePicker
                startTime={startTimeField.value}
                endTime={endTimeField.value}
                onEndTimeChange={endTimeField.setValue}
                onStartTimeChange={startTimeField.setValue}
              />

              <MoneyInput
                value={moneyField.value}
                onChange={moneyField.setValue}
              />
              <Button
                variant='bordered'
                radius='full'
                size='sm'
                className='border-1 border-gray-200 font-[500] text-gray-600 h-7'
                startContent={
                  <i className='ic-outline md-image scale-105 -mr-1 opacity-60' />
                }
                onPress={() => {
                  if (uploadImages.length) {
                    showErrMessage('等待前一张图片上传完成');
                    return;
                  }
                  setUploadImages(['CHOOSE']);
                }}
              >
                添加图片
              </Button>
            </div>
            <UploadImages
              id={id}
              list={images}
              onCancel={() => {
                setUploadImages([]);
              }}
              onSuccess={() => {
                setUploadImages([]);
              }}
            />
          </div>
        </div>

        <footer className='mt-4'>
          <Menu
            requestReorder={requestReorder}
            requestClose={requestClose}
            requestMove={() => {}}
            planId={record.get('planId')}
            date={dateField.value}
            id={id}
            onDateChange={dateField.setValue}
            icon={record.get('icon')}
          />
        </footer>
      </div>
    );
  },
  isEqual
);

const EditCustom = memo(({ id, requestClose, requestReorder }: IProps) => {
  const record = hooks.useEvents(id);
  const data = record?.get('custom');

  if (!record || !data) return null;

  return (
    <Inner
      data={data}
      requestClose={requestClose}
      requestReorder={requestReorder}
      id={id}
      record={record}
    />
  );
}, isEqual);

export default EditCustom;
